import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Button } from "./Button";

/**
 * Sur grand écran, la fiche est `fixed` (les fiches imbriquées s'empilent ainsi par-dessus
 * leur parente) : la colonne qui lui réserve sa place dans la page doit avoir exactement
 * sa largeur, dans la même unité, sinon un vide apparaît entre le tableau et la fiche.
 */
const TICKET_WIDTH = "lg:w-[33.333vw]";

/** Côté tableau : prend la place que la colonne de la fiche laisse. */
export const TICKET_SIDE = "lg:flex-1 lg:min-w-0";

/**
 * Colonne de la fiche. Toujours présente sur grand écran, à largeur nulle quand elle est
 * fermée : sa largeur peut ainsi s'animer, et le tableau à côté se redimensionne avec elle,
 * au rythme du glissement de la fiche.
 */
export const TicketColumn = ({
  open,
  children,
}: {
  open: boolean;
  children: React.ReactNode;
}) => (
  <div
    className={`shrink-0 overflow-y-auto overflow-x-hidden transition-[width] duration-300 ease-in-out motion-reduce:transition-none ${
      open
        ? `w-full h-screen ${TICKET_WIDTH} lg:border-l border-grey-light`
        : "hidden lg:block lg:w-0"
    }`}
  >
    {children}
  </div>
);

type TableDetailsProps = {
  title: string;
  icon: IconProp;
  children: React.ReactNode;
  onClose: () => void;
  onDelete?: () => void;
  deleteText?: string;
  /** Renseigné : le bouton de suppression est grisé et ce texte dit pourquoi. */
  deleteDisabledText?: string;
  useBackIcon?: boolean;
};

export const TableDetails = ({
  title,
  icon,
  children,
  onClose,
  onDelete,
  deleteText,
  deleteDisabledText = "",
  useBackIcon,
}: TableDetailsProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Déclencher l'animation d'entrée
    setIsVisible(true);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Attendre la fin de l'animation avant de fermer
  };

  return (
    <div className="fixed inset-0 lg:inset-auto z-30">
      <div
        className={`absolute lg:fixed w-full right-0 top-0 pb-16 lg:pb-2 h-full ${TICKET_WIDTH} bg-white shadow-2xl overflow-y-auto transform transition-transform duration-300 ease-in-out ${
          isVisible ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="sticky top-0 z-10 px-8 py-6 border-b border-grey-light bg-secondary text-tertiary">
          <div className="flex justify-between items-center ">
            <FontAwesomeIcon icon={icon} size="xl" />
            <h2 className="text-2xl font-oxaniumMedium">{title}</h2>
            <div className="flex gap-4">
              <button
                onClick={handleClose}
                className="text-tertiary hover:text-grey-dark transition-colors"
              >
                <FontAwesomeIcon
                  icon={useBackIcon ? "arrow-right" : "times"}
                  className="h-6 w-6"
                />
              </button>
            </div>
          </div>
        </div>

        <div>{children}</div>
        {onDelete && deleteText && (
          <>
            <Button
              type="button"
              variant="warning"
              size="full"
              className={`mt-2 text-sm ${deleteDisabledText ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={!!deleteDisabledText}
              onClick={onDelete}
            >
              {deleteText}
            </Button>
            {deleteDisabledText && (
              <p className="px-8 py-2 text-xs text-grey-dark">
                {deleteDisabledText}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/** Ligne grisée d'un ticket : liste vide, chargement ou erreur. */
export const TicketNote = ({ children }: { children: React.ReactNode }) => (
  <p className="text-center text-sm text-grey-medium">{children}</p>
);
